import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, getToken, API_BASE_URL } from '../dppApi';
import '../App.css';

const bloomMark = process.env.PUBLIC_URL + '/BloomFavicon.png';

const STEPS = ['Create', 'Details', 'Photo', 'Documents'];

const EMPTY_DETAILS = {
    description: '', metal_type: '', metal_purity: '', metal_weight_g: '',
    country_of_origin: '', care_info: '', conflict_free: false, rjc_certified: false,
};

// Brand flow: create a product (gets a QR), populate details, upload a photo,
// then upload certificates / warranty documents.
const CreateProductWizard = () => {
    const navigate = useNavigate();
    const token = getToken('brand');

    const [step, setStep] = useState(1);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);

    const [name, setName] = useState('');
    const [product, setProduct] = useState(null); // create response (product_id, qr_*)
    const [details, setDetails] = useState(EMPTY_DETAILS);
    const [photo, setPhoto] = useState(null);
    const [docs, setDocs] = useState([]);
    const [docForm, setDocForm] = useState({ doc_type: 'certificate', issued_by: '', issued_date: '', file: null });

    useEffect(() => {
        if (!token) navigate('/brand');
    }, [token, navigate]);

    const productId = product?.product_id;

    const run = async (fn) => {
        setError(null);
        setBusy(true);
        try { await fn(); } catch (e) { setError(e.message); } finally { setBusy(false); }
    };

    const createProduct = () => run(async () => {
        const data = await apiFetch('/api/products', { method: 'POST', token, body: { name } });
        setProduct(data);
        setStep(2);
    });

    const saveDetails = () => run(async () => {
        const payload = {};
        Object.entries(details).forEach(([k, v]) => {
            if (typeof v === 'boolean') { payload[k] = v; return; }
            if (v === '') return;
            payload[k] = k === 'metal_weight_g' ? Number(v) : v;
        });
        await apiFetch(`/api/products/${productId}`, { method: 'PUT', token, body: payload });
        setStep(3);
    });

    const uploadPhoto = () => run(async () => {
        if (photo) {
            const fd = new FormData();
            fd.append('image', photo);
            await apiFetch(`/api/products/${productId}/image`, { method: 'POST', token, body: fd, isForm: true });
        }
        setStep(4);
    });

    const uploadDoc = () => run(async () => {
        if (!docForm.file) throw new Error('Choose a file to upload');
        const fd = new FormData();
        fd.append('document', docForm.file);
        fd.append('doc_type', docForm.doc_type);
        if (docForm.issued_by) fd.append('issued_by', docForm.issued_by);
        if (docForm.issued_date) fd.append('issued_date', docForm.issued_date);
        const data = await apiFetch(`/api/products/${productId}/documents`, { method: 'POST', token, body: fd, isForm: true });
        setDocs([...docs, data]);
        setDocForm({ doc_type: 'certificate', issued_by: '', issued_date: '', file: null });
    });

    const updateDetail = (k) => (e) =>
        setDetails({ ...details, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });

    return (
        <div className="dpp-page">
            <div className="collection-topbar">
                <div className="collection-topbar-brand">
                    <img className="dpp-brand-mark" src={bloomMark} alt="Bloom" />
                    <h1 className="collection-topbar-title">New Product</h1>
                </div>
                <button className="dpp-link-button" onClick={() => navigate('/brand/dashboard')}>Back to dashboard</button>
            </div>

            <div className="dpp-stepper">
                {STEPS.map((label, i) => (
                    <span key={label} className={`dpp-step${step === i + 1 ? ' active' : ''}${step > i + 1 ? ' done' : ''}`}>
                        {i + 1}. {label}
                    </span>
                ))}
            </div>

            <div className="dpp-card dpp-wizard-card">
                {error && <p className="dpp-error">{error}</p>}

                {step >= 2 && product && (
                    <div className="dpp-qr-banner">
                        <img className="dpp-qr" src={`${API_BASE_URL}${product.qr_image_url}`} alt="QR code" />
                        <div>
                            <strong>{name}</strong>
                            <div className="dpp-muted">QR: {product.qr_code_id}</div>
                        </div>
                    </div>
                )}

                {step === 1 && (
                    <>
                        <h2>Create product</h2>
                        <p className="dpp-muted">A QR code is generated automatically.</p>
                        <input className="dpp-input" placeholder="Product name *" value={name}
                            onChange={(e) => setName(e.target.value)} required />
                        <button className="dpp-button" disabled={busy || !name.trim() || !!product} onClick={createProduct}>
                            {busy ? 'Creating…' : 'Create & generate QR'}
                        </button>
                    </>
                )}

                {step === 2 && (
                    <>
                        <h2>Populate details</h2>
                        <textarea className="dpp-input" placeholder="Description" value={details.description} onChange={updateDetail('description')} />
                        <input className="dpp-input" placeholder="Metal type (e.g. gold)" value={details.metal_type} onChange={updateDetail('metal_type')} />
                        <input className="dpp-input" placeholder="Metal purity (e.g. 18k)" value={details.metal_purity} onChange={updateDetail('metal_purity')} />
                        <input className="dpp-input" type="number" step="any" placeholder="Metal weight (g)" value={details.metal_weight_g} onChange={updateDetail('metal_weight_g')} />
                        <input className="dpp-input" placeholder="Country of origin" value={details.country_of_origin} onChange={updateDetail('country_of_origin')} />
                        <input className="dpp-input" placeholder="Recommended care" value={details.care_info} onChange={updateDetail('care_info')} />
                        <label className="dpp-check"><input type="checkbox" checked={details.conflict_free} onChange={updateDetail('conflict_free')} /> Conflict free</label>
                        <label className="dpp-check"><input type="checkbox" checked={details.rjc_certified} onChange={updateDetail('rjc_certified')} /> RJC certified</label>
                        <div className="dpp-wizard-nav">
                            <button className="dpp-link-button" onClick={() => setStep(1)}>Back</button>
                            <button className="dpp-button" disabled={busy} onClick={saveDetails}>{busy ? 'Saving…' : 'Save details'}</button>
                        </div>
                    </>
                )}

                {step === 3 && (
                    <>
                        <h2>Upload photo</h2>
                        <input className="dpp-input" type="file" accept="image/*" onChange={(e) => setPhoto(e.target.files[0])} />
                        <div className="dpp-wizard-nav">
                            <button className="dpp-link-button" onClick={() => setStep(2)}>Back</button>
                            <button className="dpp-button" disabled={busy} onClick={uploadPhoto}>
                                {busy ? 'Uploading…' : (photo ? 'Upload & continue' : 'Skip')}
                            </button>
                        </div>
                    </>
                )}

                {step === 4 && (
                    <>
                        <h2>Certificates &amp; Warranty</h2>
                        <select className="dpp-input" value={docForm.doc_type} onChange={(e) => setDocForm({ ...docForm, doc_type: e.target.value })}>
                            <option value="certificate">Certificate</option>
                            <option value="warranty">Warranty</option>
                            <option value="appraisal">Appraisal</option>
                            <option value="receipt">Receipt</option>
                            <option value="repair_record">Repair record</option>
                        </select>
                        <input className="dpp-input" placeholder="Issued by" value={docForm.issued_by} onChange={(e) => setDocForm({ ...docForm, issued_by: e.target.value })} />
                        <input className="dpp-input" type="date" value={docForm.issued_date} onChange={(e) => setDocForm({ ...docForm, issued_date: e.target.value })} />
                        <input className="dpp-input" type="file" onChange={(e) => setDocForm({ ...docForm, file: e.target.files[0] })} />
                        <button className="dpp-button" disabled={busy} onClick={uploadDoc}>{busy ? 'Uploading…' : 'Upload document'}</button>

                        {docs.length > 0 && (
                            <ul className="dpp-doc-list">
                                {docs.map((d) => <li key={d.doc_id}>{d.doc_type}{d.issued_by ? ` — ${d.issued_by}` : ''}</li>)}
                            </ul>
                        )}

                        <div className="dpp-wizard-nav">
                            <button className="dpp-link-button" onClick={() => setStep(3)}>Back</button>
                            <button className="dpp-button dpp-finish" onClick={() => navigate('/brand/dashboard')}>Finish</button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default CreateProductWizard;
